import React, { useState, useEffect } from 'react';
import { ScrollView, View } from 'react-native';
import Typography from 'src/components/Text/Text';
import InputFields from './InputFields';
import styles from './CompanyDetailsScreen.styles';
import { useLinkTo, useRoute } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/store';
import { Company } from '@typeRoots/common/company';
import { initialCompanyState } from 'src/features/company/slice/initialState';
import { updateCompany } from 'src/features/company/slice/companySlice';
import { URLS } from 'src/constants/urls';
import Button from 'src/components/Button/Button';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { trackCompanyGTMEvent } from 'src/features/company/helpers/gtmHelpers';
import { useUpdateUserMutation } from 'src/features/user/api/userApi';
import { useRegisterUserMutation } from 'src/features/authentication/api/authenticationApi';
import { setUserToken } from 'src/features/user/slice/userSlice';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getCompanyDetailsTour } from 'src/features/tour/tourSteps';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';

interface RouteParams {
  garagingState?: string;
  token?: string;
}

const CompanyDetailsScreen: React.FC = () => {
  const linkTo = useLinkTo();
  const dispatch = useDispatch();
  const route = useRoute();

  const company = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const userToken = useSelector((state: RootState) => state.UI.tpJwtToken);
  const accountSource = useSelector((state: RootState) => state.UI.accountSource);
  const currentUser = useSelector((state: RootState) => state.users.currentUser);

  // Check if user came from garaging address flow (no DOT)
  const garagingState = (route.params as RouteParams)?.garagingState;
  const hideDotField = !!garagingState;

  const [formData, setFormData] = useState<Company>(company || { ...initialCompanyState });
  const [registerUser] = useRegisterUserMutation();
  const [updateUser] = useUpdateUserMutation();

  useTourAutoStart({
    tourKey: 'companyDetails',
    driverSteps: getCompanyDetailsTour(),
    enabled: !!company,
  });

  useEffect(() => {
    // Track GTM event for new company or editing existing company
    if (company && accountType === 'user') {
      trackCompanyGTMEvent(company, accountType);
    }
  }, []);

  useEffect(() => {
    // Handle garaging state when user comes from no-DOT flow
    if (garagingState && currentUser && userToken) {
      const userWithGaragingState = {
        ...currentUser,
        garagingState,
      };

      // If user already exists (has an id), update them
      // Otherwise, create a new user
      if (currentUser.id) {
        // User exists - update with new/changed garaging state
        updateUser({ id: currentUser.id, user: userWithGaragingState })
          .unwrap()
          .then(() => {
            console.log('User updated with garaging state:', garagingState);
          })
          .catch((error) => {
            console.error('Error updating user with garaging state:', error);
          });
      } else {
        // New user - create with garaging state
        registerUser({
          newUser: userWithGaragingState,
          token: userToken,
          dotNumber: '', // No DOT in this flow
          accountSource,
        })
          .unwrap()
          .then((userData) => {
            console.log('User created with garaging state:', garagingState);
            // Store the user token for authenticated API calls
            if (userData.token) {
              dispatch(setUserToken(userData.token));
            }
          })
          .catch((error) => {
            console.error('Error creating user with garaging state:', error);
          });
      }
    }
  }, [garagingState]);

  useEffect(() => {
    // Sync form data with Redux on load
    if (company) {
      setFormData(company);
    }
  }, [company]);

  // Handle input changes locally
  const handleInputChange = (field: keyof Company, value: string | number | boolean) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
  };

  // Reset form data and navigate back
  const handleCancel = () => {
    // Track when edit is cancelled
    trackGTMEvent(
      'company-details-screen-canceled',
      {
        buttonTitle: 'Cancel',
        screenName: 'CompanyDetailsScreen',
      },
      accountType,
    );
    setFormData(company || { ...initialCompanyState }); // Reset form data
    linkTo(URLS.COMPANY.LANDING); // Navigate back to landing screen
  };

  // Save changes to Redux and navigate to the next screen
  const handleNext = () => {
    // Track when changes are confirmed
    trackGTMEvent(
      'company-details-confirmed',
      {
        buttonTitle: 'Next',
        screenName: 'CompanyDetailsScreen',
      },
      accountType,
    );
    // Save formData to Redux and mark as dirty
    dispatch(updateCompany(formData));
    linkTo(URLS.COMPANY.ADDRESS); // Navigate to the address screen
  };

  // Disable "Next" button if any required fields are empty
  const isNextDisabled = !formData.companyName || !formData.dateBusinessStarted;

  return (
    <ScrollView
      contentContainerStyle={styles.welcomeDetailsContainer}
      keyboardShouldPersistTaps="handled"
      bounces={false}>
      <View style={styles.dataFieldsContainer}>
        <Typography variant="h2" style={styles.title}>
          Tell us what we missed!
        </Typography>
        {/* Always render InputFields */}
        <InputFields formData={formData} onChange={handleInputChange} hideDotField={hideDotField} />
      </View>
      <View style={styles.buttonContainer}>
        <View style={styles.buttonWrapper1}>
          <Button
            testID="landing-cancel-btn-edit"
            type="tertiary"
            size="big"
            colorVariant="default"
            title="Cancel"
            onPress={handleCancel}
          />
        </View>
        <View style={styles.buttonWrapper2}>
          <TourWrapper tourId="continue-btn">
            <Button
              testID="landing-next-btn-edit"
              type="primary"
              title="Next"
              size="big"
              colorVariant={isNextDisabled ? 'disabled' : 'default'}
              onPress={handleNext}
              disabled={isNextDisabled} // Disable button if required fields are empty
            />
          </TourWrapper>
        </View>
      </View>
    </ScrollView>
  );
};

export default CompanyDetailsScreen;
